import { z } from 'zod';
import { SegmentRules } from './segments.ts';

/**
 * Automations: "when this happens, do these steps". Definitions are data (validated here) so ready-made recipes
 * and ones built on screen run through the same engine (workflow-workers). Each automation is automatic, or in
 * manual mode where every message it would send waits for a person to approve.
 */
export const TRIGGERS = [
  'order.created',
  'order.paid',
  'order.fulfilled',
  'order.delivered',
  'order.cancelled',
  'checkout.abandoned',
  'product.back_in_stock',
  'replenish.due',
  'birthday',
  'customer.lapsing',
  'lead.created',
] as const;
export type TriggerType = (typeof TRIGGERS)[number];
/** Triggers found by a daily look at the data rather than by an event. */
export const SCHEDULED_TRIGGERS: readonly TriggerType[] = ['replenish.due', 'birthday', 'customer.lapsing'];

export const Trigger = z.object({
  type: z.enum(TRIGGERS),
  /** customer.lapsing: days since the last purchase. */
  days: z.number().int().min(14).max(720).optional(),
});

/** What the message says: a WhatsApp template (with its {{1}}… values), and/or free text per language. */
export const SendStep = z.object({
  type: z.literal('send'),
  channels: z
    .array(z.enum(['whatsapp', 'email', 'sms']))
    .min(1)
    .max(3),
  template: z
    .string()
    .regex(/^[a-z0-9_]{1,512}$/)
    .nullish(),
  params: z.array(z.string().max(40)).max(10).optional(),
  text: z.object({ en: z.string().max(4000).nullish(), ar: z.string().max(4000).nullish() }).nullish(),
  subject: z.object({ en: z.string().max(300).nullish(), ar: z.string().max(300).nullish() }).nullish(),
});
export const Step = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('wait'),
    minutes: z
      .number()
      .int()
      .min(1)
      .max(60 * 24 * 60),
  }),
  SendStep,
  z.object({
    type: z.literal('stop_if'),
    condition: z.enum(['ordered_since', 'replied_since', 'checkout_recovered', 'order_cancelled']),
  }),
  z.object({ type: z.literal('tag'), tags: z.array(z.string().trim().min(1).max(40)).min(1).max(5) }),
  z.object({
    type: z.literal('task'),
    title: z.string().trim().min(1).max(200),
    dueMinutes: z
      .number()
      .int()
      .min(0)
      .max(60 * 24 * 30)
      .default(60),
  }),
]);
export type Step = z.infer<typeof Step>;

export const AutomationDef = z.object({
  name: z.string().trim().min(1).max(200),
  nameAr: z.string().trim().max(200).nullish(),
  trigger: Trigger,
  conditions: SegmentRules.default({ all: [] }),
  steps: z.array(Step).min(1).max(12),
  purpose: z.enum(['service', 'marketing']),
  mode: z.enum(['auto', 'manual']).default('auto'),
  attributionDays: z.number().int().min(1).max(90).default(7),
});
export type AutomationDef = z.infer<typeof AutomationDef>;

/** Variables a message can use, by trigger. */
export const VARIABLES: Record<TriggerType, string[]> = {
  'order.created': ['name', 'order', 'total', 'store'],
  'order.paid': ['name', 'order', 'total', 'store'],
  'order.fulfilled': ['name', 'order', 'tracking', 'trackingUrl', 'store'],
  'order.delivered': ['name', 'order', 'store'],
  'order.cancelled': ['name', 'order', 'store'],
  'checkout.abandoned': ['name', 'link', 'total', 'store', 'code'],
  'product.back_in_stock': ['name', 'product', 'price', 'store'],
  'replenish.due': ['name', 'product', 'store', 'code'],
  birthday: ['name', 'store', 'code'],
  'customer.lapsing': ['name', 'store', 'code'],
  'lead.created': ['name', 'store'],
};

/** Promotional messages are not sent at night (local time); they wait until the morning. */
export const QUIET_HOURS = { from: 22, to: 9 } as const;

/** The next time a promotional message may be sent, in the tenant's zone (now if outside quiet hours). */
export function nextSendTime(now: Date, timeZone: string): Date {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hourCycle: 'h23' }).format(now),
  );
  if (hour >= QUIET_HOURS.to && hour < QUIET_HOURS.from) return now;
  const hoursUntil = hour >= QUIET_HOURS.from ? 24 - hour + QUIET_HOURS.to : QUIET_HOURS.to - hour;
  const t = new Date(now.getTime() + hoursUntil * 3_600_000);
  t.setUTCMinutes(0, 0, 0);
  return t;
}

/** At most one promotional automation message per person per this many hours. */
export const MARKETING_GAP_HOURS = 20;

export interface Recipe extends AutomationDef {
  key: string;
}

const send = (
  template: string,
  params: string[],
  channels: ('whatsapp' | 'email' | 'sms')[] = ['whatsapp', 'email'],
  text?: { en: string; ar: string },
) =>
  SendStep.parse({
    type: 'send',
    channels,
    template,
    params,
    ...(text && { text, subject: { en: '{{store}}', ar: '{{store}}' } }),
  });

/** Ready-made automations every organisation gets (off until switched on). */
export const RECIPES: Recipe[] = [
  {
    key: 'order_confirmation',
    name: 'Order confirmation',
    nameAr: 'تأكيد الطلب',
    purpose: 'service',
    mode: 'auto',
    attributionDays: 7,
    trigger: { type: 'order.created' },
    conditions: { all: [] },
    steps: [
      send('order_confirmation', ['name', 'order', 'total'], ['whatsapp', 'email'], {
        en: 'Hi {{name}}, thank you for your order {{order}} ({{total}}). We will message you when it is on its way.',
        ar: 'أهلًا {{name}}، شكرًا لطلبك {{order}} ({{total}}). سنراسلك عند شحنه.',
      }),
    ],
  },
  {
    key: 'shipping_update',
    name: 'Order on its way',
    nameAr: 'الطلب في الطريق',
    purpose: 'service',
    mode: 'auto',
    attributionDays: 7,
    trigger: { type: 'order.fulfilled' },
    conditions: { all: [] },
    steps: [
      send('order_shipped', ['name', 'order', 'trackingUrl'], ['whatsapp', 'email'], {
        en: 'Good news {{name}}: your order {{order}} is on its way. Track it here: {{trackingUrl}}',
        ar: 'خبر سار {{name}}: طلبك {{order}} في الطريق. تتبّعه من هنا: {{trackingUrl}}',
      }),
    ],
  },
  {
    key: 'review_request',
    name: 'Review request after delivery',
    nameAr: 'طلب تقييم بعد التسليم',
    purpose: 'marketing',
    mode: 'auto',
    attributionDays: 7,
    trigger: { type: 'order.delivered' },
    conditions: { all: [] },
    steps: [{ type: 'wait', minutes: 2 * 24 * 60 }, send('review_request', ['name', 'order'])],
  },
  {
    key: 'abandoned_checkout',
    name: 'Abandoned checkout reminder',
    nameAr: 'تذكير بالسلة المتروكة',
    purpose: 'marketing',
    mode: 'auto',
    attributionDays: 3,
    trigger: { type: 'checkout.abandoned' },
    conditions: { all: [] },
    steps: [
      { type: 'wait', minutes: 60 },
      { type: 'stop_if', condition: 'checkout_recovered' },
      send('cart_reminder', ['name', 'link']),
      { type: 'wait', minutes: 23 * 60 },
      { type: 'stop_if', condition: 'checkout_recovered' },
      send('cart_reminder_offer', ['name', 'link', 'code']),
    ],
  },
  {
    key: 'back_in_stock',
    name: 'Back in stock',
    nameAr: 'عاد إلى المخزون',
    // The customer asked to be told: a service message.
    purpose: 'service',
    mode: 'auto',
    attributionDays: 7,
    trigger: { type: 'product.back_in_stock' },
    conditions: { all: [] },
    steps: [send('back_in_stock', ['name'])],
  },
  {
    key: 'refill_reminder',
    name: 'Refill reminder',
    nameAr: 'تذكير بإعادة الشراء',
    purpose: 'marketing',
    mode: 'auto',
    attributionDays: 14,
    trigger: { type: 'replenish.due' },
    conditions: { all: [] },
    steps: [send('refill_reminder', ['name', 'product'])],
  },
  {
    key: 'birthday_offer',
    name: 'Birthday offer',
    nameAr: 'عرض عيد الميلاد',
    purpose: 'marketing',
    mode: 'auto',
    attributionDays: 14,
    trigger: { type: 'birthday' },
    conditions: { all: [] },
    steps: [send('birthday_offer', ['name', 'code'])],
  },
  {
    key: 'win_back',
    name: 'Win back after 4 months',
    nameAr: 'استعادة العميل بعد ٤ أشهر',
    purpose: 'marketing',
    mode: 'manual',
    attributionDays: 14,
    trigger: { type: 'customer.lapsing', days: 120 },
    conditions: { all: [{ type: 'orders', min: 2 }] },
    steps: [send('win_back', ['name', 'code'])],
  },
  {
    key: 'ad_lead_followup',
    name: 'Follow up chats from ads',
    nameAr: 'متابعة محادثات الإعلانات',
    purpose: 'service',
    mode: 'auto',
    attributionDays: 7,
    trigger: { type: 'lead.created' },
    conditions: { all: [] },
    steps: [
      { type: 'tag', tags: ['ad-lead'] },
      { type: 'wait', minutes: 15 },
      { type: 'stop_if', condition: 'replied_since' },
      { type: 'task', title: 'Reply to the chat from an ad', dueMinutes: 15 },
    ],
  },
];

/** The WhatsApp templates the recipes use (to register for approval with Meta). */
export const RECIPE_TEMPLATES = [
  ...new Set(
    RECIPES.flatMap((r) => r.steps.flatMap((s) => (s.type === 'send' && s.template ? [s.template] : []))),
  ),
];
